import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RichTextDescription } from './index';
import { DescriptionModal } from './components/DescriptionModal';
import { MarkdownPreview } from './components/MarkdownPreview';
import { CypressWrappedComponent } from './components/CypressWrappedComponent';
import { Preview } from './components/Preview';
import { Editor } from './components/Editor';
import type { MarkdownMentionPreviewContext, ShortcutConfig } from './types';
import cx from 'classnames';
import styles from './TaskDescriptionField.module.css';

export const TASK_DESCRIPTION_CYPRESS_TRIGGER = 'taskDescription';

export const DescriptionFieldSkeleton = () => (
  <div className={styles.skeleton} data-cy="task-description-skeleton">
    <div className={cx(styles.skeletonLine, styles.skeletonLineWide)} />
    <div className={cx(styles.skeletonLine, styles.skeletonLineMedium)} />
    <div className={cx(styles.skeletonLine, styles.skeletonLineShort)} />
  </div>
);

export type TaskDescriptionFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  mode?: 'inline' | 'compact' | 'details';
  editable?: boolean;
  shortcutConfig?: ShortcutConfig | null;
  submitTask?: () => void;
  fullHeight?: boolean;
  getContainer?: () => HTMLElement;
  mentionPreviewContext?: MarkdownMentionPreviewContext;
  fixedWidthDetailsToolbarColumn?: boolean;
  show?: boolean;
  footerActions?: React.ReactNode;
};

export const TaskDescriptionField = ({
  value,
  onChange,
  placeholder = 'Add description',
  mode = 'compact',
  editable = true,
  shortcutConfig = null,
  submitTask,
  fullHeight = false,
  getContainer,
  mentionPreviewContext,
  fixedWidthDetailsToolbarColumn = false,
  show = true,
  footerActions
}: TaskDescriptionFieldProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [undoValue, setUndoValue] = useState<string | null>(null);
  const lastOwnSavedRef = useRef<string>(value);
  const mentionOrDerived =
    mentionPreviewContext ?? (value ? { descriptionMarkdown: value } : undefined);

  useEffect(() => {
    if (value !== lastOwnSavedRef.current) {
      setUndoValue(null);
      lastOwnSavedRef.current = value;
    }
  }, [value]);

  const handleDetailsSave = useCallback((newValue: string) => {
    if (newValue === lastOwnSavedRef.current) return;
    setUndoValue(lastOwnSavedRef.current);
    lastOwnSavedRef.current = newValue;
    onChange(newValue);
  }, [onChange]);

  const handleUndo = useCallback(() => {
    if (undoValue === null) return;
    const target = undoValue;
    lastOwnSavedRef.current = target;
    setUndoValue(null);
    onChange(target);
  }, [undoValue, onChange]);

  const outerClassName = cx(
    styles.descriptionFieldOuter, {
      [styles.fullHeightDetails]: mode === 'details' && fullHeight,
      [styles.hidden]: !show
    }
  );

  if (!['details', 'inline'].includes(mode)) {
    return (
      <div className={outerClassName}>
        <CypressWrappedComponent
          show={show}
          showComponentForMode={true}
          className={styles.tdfCompactWrapper}
          onClick={() => editable && setModalOpen(true)}
          role="button"
          tabIndex={editable ? 0 : undefined}
          onKeyDown={e => editable && (e.key === 'Enter' || e.key === ' ') && setModalOpen(true)}
        >
          <MarkdownPreview
            content={value || (editable ? placeholder : 'No description')}
            className={value ? styles.tdfHasContent : styles.tdfPlaceholder}
            mentionPreviewContext={mentionOrDerived}
          />
          {editable && (
            <span className={styles.tdfEditHint}>Click to view or edit</span>
          )}
        </CypressWrappedComponent>
        <DescriptionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          value={value}
          onChange={onChange}
          onSave={onChange}
          shortcutConfig={shortcutConfig}
          title="Task description"
          getContainer={getContainer}
          mentionRenderContext={mentionOrDerived ?? null}
        />
      </div>
    )
  }

  return (
    <div
      className={outerClassName}
    >
      <CypressWrappedComponent show={show} showComponentForMode={mode === 'inline'}>
        <RichTextDescription
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          fullHeight={fullHeight}
          shortcutConfig={shortcutConfig}
          submitTask={submitTask}
          mentionRenderContext={mentionOrDerived ?? null}
          getContainer={getContainer}
        />
      </CypressWrappedComponent>
      <CypressWrappedComponent
        show={show}
        showComponentForMode={mode === 'details'}
        className={editable ? cx(styles.tdfDetailsModeRoot, {
          [styles.tdfDetailsModeRootFill]: fullHeight
        }) : undefined}
      >
        <Preview
          show={!editable}
          content={value || 'No description'}
          className={value ? undefined : styles.tdfPlaceholder}
          mentionPreviewContext={mentionOrDerived}
        />
        {!editable && footerActions && (
          <div className={styles.tdfFooterActions}>{footerActions}</div>
        )}
        <Editor
          footerActions={footerActions}
          show={editable}
          value={value}
          placeholder={placeholder}
          fullHeight={fullHeight}
          fixedWidthDetailsToolbarColumn={fixedWidthDetailsToolbarColumn}
          undoValue={undoValue}
          handleDetailsSave={handleDetailsSave}
          handleUndo={handleUndo}
          mentionOrDerived={mentionOrDerived}
          getContainer={getContainer}
        />
      </CypressWrappedComponent>
    </div>
  );
};
